import React, { useState, useEffect, useMemo } from 'react';
import Icon from './icons';
import { MapContainer, TileLayer, Marker, Popup, Polyline, CircleMarker, Tooltip, Rectangle, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { useIsMobile } from './hooks';
import { latLonToGrid, haversineKm } from './utils';
import { calculateSolarPosition, solarElevation } from './shared';
import { useI18n, tx } from './i18n';

// ── Mapy (wydzielone z App.js) ───────────────────────────────────────────────
// QSOMap (mapa lacznosci + grayline), MaidenheadGridLayer, QsoLocatorMap.
// Wspoldzielone przez wiele widokow (mapa QSO, PSK Reporter, sasiedzi).

export function GraylineLayer() {
  const map = useMap();
  const [bounds, setBounds] = useState(map.getBounds());
  const [now, setNow] = useState(new Date());

  useMapEvents({
    moveend: () => setBounds(map.getBounds()),
    zoomend: () => setBounds(map.getBounds()),
  });

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60000); // przelicz co minute
    return () => clearInterval(t);
  }, []);

  const { declination, subsolarLon } = calculateSolarPosition(now);
  const step = 4; // stopnie -- rozdzielczosc siatki cieniowania
  const south = Math.max(-90, bounds.getSouth());
  const north = Math.min(90, bounds.getNorth());
  const west = bounds.getWest();
  const east = bounds.getEast();

  const cellCount = Math.ceil((east - west) / step) * Math.ceil((north - south) / step);
  if (cellCount > 4000) return null; // zabezpieczenie przed zbyt szerokim widokiem

  const cells = [];
  const startLon = Math.floor(west / step) * step;
  for (let lon = startLon; lon < east; lon += step) {
    for (let lat = Math.floor(south / step) * step; lat < north; lat += step) {
      const elev = solarElevation(lat + step / 2, lon + step / 2, declination, subsolarLon);
      if (elev >= -6) continue; // dzień lub zmierzch cywilny -- nie cieniujemy
      const opacity = elev < -12 ? 0.45 : 0.22; // pelna noc vs zmierzch
      cells.push(
        <Rectangle key={`${lon}_${lat}`} bounds={[[lat, lon], [lat + step, lon + step]]}
          pathOptions={{ fillColor: '#000010', fillOpacity: opacity, stroke: false, interactive: false }} />
      );
    }
  }
  return <>{cells}</>;
}

// Grupowanie punktow w kwadraty siatki, których rozmiar zaleznie od zoomu --
// zamiast twardego limitu liczby rysowanych punktow (dawniej: obcinanie po
// 500, reszta po prostu znikala z mapy). Kazdy kwadrat z >1 QSO rysuje się
// jako JEDEN znacznik z liczba łączności -- caly log jest wiec zawsze
// widoczny, tylko gesto skupione punkty zlewaja się w jeden marker zamiast
// setek nakladajacych się na siebie warstw SVG (to wlasnie zabijalo
// wydajnosc, nie sama liczba QSO w bazie).
function clusterPoints(points, zoom) {
  // Kwadrat siatki polowi się z kazdym poziomem zoomu (standardowa konwencja
  // kafelkow mapowych: swiat = 360 stopni dlugosci geogr. na zoomie 0).
  const bucketDeg = Math.max(0.02, 20 / Math.pow(2, zoom));
  const buckets = new Map();
  for (const q of points) {
    const key = `${Math.floor(q.lat / bucketDeg)}_${Math.floor(q.lon / bucketDeg)}`;
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(q);
  }
  return [...buckets.values()].map(items => {
    const lat = items.reduce((s,q)=>s+q.lat,0) / items.length;
    const lon = items.reduce((s,q)=>s+q.lon,0) / items.length;
    return { lat, lon, items };
  });
}

function ClusteredQsoMarkers({ points, home }) {
  const { t } = useI18n();
  const map = useMap();
  const [zoom, setZoom] = useState(map.getZoom());
  useMapEvents({ zoomend: () => setZoom(map.getZoom()) });

  const clusters = useMemo(() => clusterPoints(points, zoom), [points, zoom]);

  return clusters.map((c, i) => (
    c.items.length === 1 ? (
      <React.Fragment key={i}>
        <Polyline positions={[home,[c.lat,c.lon]]} color="#00b8ff" weight={1} opacity={0.4} />
        <CircleMarker center={[c.lat,c.lon]} radius={5} fillColor="#00b8ff" color="#fff" weight={1} fillOpacity={0.9}>
          <Popup>
            <strong>{c.items[0].callsign}</strong><br/>
            {c.items[0].country}<br/>
            {c.items[0].frequency} {tx('MHz ·')} {c.items[0].mode}
          </Popup>
        </CircleMarker>
      </React.Fragment>
    ) : (
      <CircleMarker key={i} center={[c.lat,c.lon]}
        radius={Math.min(22, 8 + Math.log2(c.items.length) * 3)}
        fillColor="#ffbb00" color="#fff" weight={1} fillOpacity={0.85}>
        <Tooltip permanent direction="center" className="map-cluster-count">{c.items.length}</Tooltip>
        <Popup>
          <strong>{c.items.length} {t('maps.qsoInArea')}</strong>
          <ul style={{margin:'6px 0 0',padding:'0 0 0 16px',maxHeight:140,overflowY:'auto'}}>
            {c.items.slice(0,20).map((q,j)=>(
              <li key={j}>{q.callsign} — {q.band||''} {q.mode}</li>
            ))}
            {c.items.length>20&&<li>{t('maps.andMorePrefix')} {c.items.length-20} {t('maps.andMoreSuffix')}</li>}
          </ul>
        </Popup>
      </CircleMarker>
    )
  ));
}

export function QSOMap({ qsos, homePos, theme }) {
  const { t } = useI18n();
  const qsoPoints = qsos.filter(q=>q.lat&&q.lon);
  // UWAGA na pulapke: [undefined, undefined] to PRAWDZIWA tablica, wiec zwykle
  // `homePos || domyslne` NIE zadziala. User, który nie ustawil swojej pozycji,
  // dostawal wtedy od Leafleta "Invalid LatLng object" i CALA mapa się wywalala.
  // Dlatego sprawdzamy same wspolrzedne, a nie obecnosc tablicy.
  const validHome = Array.isArray(homePos)
    && Number.isFinite(Number(homePos[0])) && Number.isFinite(Number(homePos[1]));
  const home = validHome ? [Number(homePos[0]), Number(homePos[1])] : [52.3, 16.9];
  const homeIcon = L.divIcon({ className:'', html:'<div style="width:14px;height:14px;background:#00e5a0;border:2px solid #fff;border-radius:50%;box-shadow:0 0 8px #00e5a0"></div>', iconAnchor:[7,7] });
  const [showGrayline, setShowGrayline] = useState(false);
  const isMobile = useIsMobile();

  return (
    <div style={{position:'relative'}}>
      <button type="button" className="btn btn--secondary btn--sm"
        style={{position:'absolute',top:10,right:10,zIndex:500}}
        onClick={()=>setShowGrayline(s=>!s)}>
        <Icon name="sun" size={16} /> {tx('Grayline')} {showGrayline?t('maps.graylineOn'):t('maps.graylineOff')}
      </button>
      <MapContainer center={home} zoom={isMobile?2:3} style={{height:isMobile?'320px':'500px',width:'100%',background:'#0a1520'}}
        className="leaflet-dark">
        <TileLayer
          url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}"
          attribution='Tiles &copy; Esri'
        />
        {showGrayline&&<GraylineLayer />}
        <Marker position={home} icon={homeIcon}>
          <Popup><strong>{t('maps.yourPosition')}</strong></Popup>
        </Marker>
        <ClusteredQsoMarkers points={qsoPoints} home={home} />
      </MapContainer>
    </div>
  );
}

// ── Maidenhead Grid Layer (siatka lokatorow rysowana na mapie) ───────────────
function MaidenheadGridLayer() {
  const map = useMap();
  const [bounds, setBounds] = useState(map.getBounds());
  const [zoom, setZoom] = useState(map.getZoom());

  useMapEvents({
    moveend: () => { setBounds(map.getBounds()); setZoom(map.getZoom()); },
    zoomend: () => { setBounds(map.getBounds()); setZoom(map.getZoom()); },
  });

  // Przy malym zoomie pokazuj tylko pola (20°x10°), przy wiekszym -- kwadraty (2°x1°)
  const showSquares = zoom >= 5;
  const step_lon = showSquares ? 2 : 20;
  const step_lat = showSquares ? 1 : 10;

  const south = Math.max(-90, bounds.getSouth());
  const north = Math.min(90, bounds.getNorth());
  const west = Math.max(-180, bounds.getWest());
  const east = Math.min(180, bounds.getEast());

  // Limit liczby linii, by nie zamulic renderu przy bardzo szerokim widoku
  const lonCount = Math.ceil((east - west) / step_lon);
  const latCount = Math.ceil((north - south) / step_lat);
  if (lonCount > 60 || latCount > 60) return null;

  const lines = [];
  const labels = [];

  const startLon = Math.floor(west / step_lon) * step_lon;
  for (let lon = startLon; lon <= east; lon += step_lon) {
    lines.push(<Polyline key={'v'+lon} positions={[[south,lon],[north,lon]]} color="#00e5a0" weight={0.6} opacity={0.35} />);
  }
  const startLat = Math.floor(south / step_lat) * step_lat;
  for (let lat = startLat; lat <= north; lat += step_lat) {
    lines.push(<Polyline key={'h'+lat} positions={[[lat,west],[lat,east]]} color="#00e5a0" weight={0.6} opacity={0.35} />);
  }

  // Etykiety lokatorow w srodku kazdej komorki
  for (let lon = startLon; lon < east; lon += step_lon) {
    for (let lat = startLat; lat < north; lat += step_lat) {
      const cLat = lat + step_lat/2, cLon = lon + step_lon/2;
      const label = latLonToGrid(cLat, cLon, showSquares ? 4 : 2).slice(0, showSquares ? 4 : 2);
      const icon = L.divIcon({
        className: '',
        html: `<div style="color:#00e5a0;font-family:monospace;font-size:${showSquares?10:13}px;font-weight:700;opacity:0.7;white-space:nowrap;text-shadow:0 0 4px #000,0 0 4px #000">${label}</div>`,
        iconSize: [60,16], iconAnchor:[30,8],
      });
      labels.push(<Marker key={'lbl'+lon+'_'+lat} position={[cLat,cLon]} icon={icon} interactive={false} />);
    }
  }

  return <>{lines}{labels}</>;
}

// ── QSO Locator Map (mapka w formularzu loggera) ──────────────────────────────
export function QsoLocatorMap({ homePos, dxPos, dxCallsign, homeGrid, dxGrid, theme }) {
  const { t } = useI18n();
  const home = homePos || [52.3, 16.9];
  const hasDx = dxPos && dxPos.lat != null && dxPos.lon != null;
  const dist = hasDx ? haversineKm(home[0], home[1], dxPos.lat, dxPos.lon) : null;

  const homeIcon = L.divIcon({ className:'', html:'<div style="width:14px;height:14px;background:#00e5a0;border:2px solid #fff;border-radius:50%;box-shadow:0 0 8px #00e5a0"></div>', iconAnchor:[7,7] });
  const dxIcon = L.divIcon({ className:'', html:'<div style="width:14px;height:14px;background:#ff6b35;border:2px solid #fff;border-radius:50%;box-shadow:0 0 8px #ff6b35"></div>', iconAnchor:[7,7] });

  // Wybierz srodek/zoom tak by objac obie stacje
  const center = hasDx ? [(home[0]+dxPos.lat)/2, (home[1]+dxPos.lon)/2] : home;

  return (
    <div className="qso-locator-map">
      <div className="qso-locator-map__header">
        <span className="qso-locator-map__title"><Icon name="pin" size={18} /> {t('maps.locatorGridTitle')}</span>
        {hasDx && (
          <span className="qso-locator-map__distance">
            {Math.round(dist)} {tx('km')}
          </span>
        )}
      </div>
      <MapContainer center={center} zoom={hasDx?5:4} style={{height:'260px',width:'100%',background:'#0a1520'}}
        className="leaflet-dark" key={hasDx?`${dxPos.lat}-${dxPos.lon}`:'home-only'}>
        <TileLayer
          url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}"
          attribution='Tiles &copy; Esri'
        />
        <MaidenheadGridLayer />
        <Marker position={home} icon={homeIcon}>
          <Popup><strong>{t('maps.you')}</strong>{homeGrid?<><br/>{homeGrid}</>:null}</Popup>
        </Marker>
        {hasDx && (
          <>
            <Marker position={[dxPos.lat, dxPos.lon]} icon={dxIcon}>
              <Popup><strong>{dxCallsign}</strong>{dxGrid?<><br/>{dxGrid}</>:null}</Popup>
            </Marker>
            <Polyline positions={[home,[dxPos.lat,dxPos.lon]]} color="#ff6b35" weight={2} opacity={0.7} dashArray="4,4">
              <Tooltip permanent direction="center" className="qso-locator-distance-tooltip">
                {Math.round(dist)} {tx('km')}
              </Tooltip>
            </Polyline>
          </>
        )}
      </MapContainer>
      {!hasDx && (
        <div className="qso-locator-map__hint">
          {t('maps.lookupHint')}
        </div>
      )}
    </div>
  );
}

// ── Propagation Bar ───────────────────────────────────────────────────────────



